import formatPercent from "../../hooks/formatPercent";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaMinus, FaPlus } from "react-icons/fa";
import BreadcrumbPurchase from "./purchaseComponents/BreadcrumbPurchase";
import PurchaseHeader from "./purchaseComponents/PurchaseHeader";
import { getProductDetailPurchasePbf } from "../../../services/purchaseApi";
import SuccessAddCartPopup from "./purchaseComponents/SuccessAddCartPopup";
import ErrorPopup from "../../common/particles.jsx/ErrorPopup";
import { useAddToCartPbf } from "../../hooks/purchaseHook/addToCartPbf";
import { getAppliedTier } from "./purchaseComponents/CardProduct";
import moment from "moment";
import PbfRefusalDialog from "./purchaseComponents/PbfRefusalDialog";
import {
  isPbfRefusal,
  quotaCapHint,
  quotaDetailLabel,
  stepperMax,
} from "./purchaseComponents/pbfInventoryText";

const toIDR = (val) =>
  val?.toLocaleString("id-ID", {
    style: "currency",
    currency: "IDR",
  });

const PurchaseProductDetail = () => {
  const { id } = useParams();
  const { type } = useParams();

  const [previewImg, setPreviewImg] = useState(0);
  const [orderQty, setOrderQty] = useState(1);
  const [errorMessage, setErrorMessage] = useState("");
  const [openSuccess, setOpenSuccess] = useState(null);
  const [refusal, setRefusal] = useState(null);

  const addToCart = useAddToCartPbf();

  const handleAddToCart = () => {
    var body = {
      product_uuid: dataDetail.uuid,
      qty: orderQty,
    };

    addToCart.mutate(body, {
      onSuccess: () => {
        setOpenSuccess(true);
      },
      onError: (err) => {
        if (isPbfRefusal(err)) setRefusal(err);
        else setErrorMessage(err.message.id ? err.message.id : err.message);
      },
    });
  };

  // Same rule as checkout: the one tier covering orderQty applies to every
  // unit; above a capped top tier there is no discount.
  const getPricePerPiece = () => {
    const tier = getAppliedTier(dataDetail.discount, orderQty);
    return tier
      ? dataDetail.base_price * ((100 - tier.percent) / 100)
      : dataDetail.base_price;
  };

  const {
    data: dataDetail,
    isFetching: isFetchingDetail,
    refetch,
  } = useQuery({
    queryKey: ["detail-purchase", id],
    queryFn: () => getProductDetailPurchasePbf(id),
    enabled: id !== undefined,
    refetchOnWindowFocus: false,
  });

  const quota = dataDetail?.quota;
  const maxQty = stepperMax(dataDetail?.stock, quota);

  useEffect(() => {
    if (dataDetail && !isFetchingDetail && maxQty === 0) setOrderQty(0);
  }, [dataDetail]);

  return (
    <div className="text-[12px] text-[#2C3E5D]">
      <PurchaseHeader />

      <div className="mt-[70px]">
        <div className="px-3 pt-2 mb-4">
          <BreadcrumbPurchase
            productName={dataDetail && dataDetail ? dataDetail.name : null}
          />
        </div>

        <div className="px-3 mb-4">
          {dataDetail && !isFetchingDetail && dataDetail ? (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 md:bg-white rounded-md">
                <div className="w-full relative bg-white rounded-md py-3 px-4">
                  <div className="h-fit relative mb-2">
                    <img
                      src={dataDetail.images[previewImg]}
                      className="object-contain rounded-lg mb-2"
                    />
                    {dataDetail.stock === 0 ? (
                      <div className="bg-[#FFE7E7] text-[#C62828] font-bold w-[80px] h-[80px] rounded-full absolute top-0 bottom-0 left-0 right-0 my-auto mx-auto flex justify-center items-center">
                        Habis
                      </div>
                    ) : null}
                  </div>
                  <div className="flex gap-4">
                    {dataDetail.images.map((item, i) => {
                      return (
                        <img
                          src={item}
                          className={`w-[70px] h-[70px] object-contain rounded-lg cursor-pointer hover:shadow-md ${
                            i === previewImg ? "border-2 border-[#94C679]" : ""
                          }`}
                          onClick={() => setPreviewImg(i)}
                        />
                      );
                    })}
                  </div>
                </div>
                <div className="w-full md:col-span-2 bg-white rounded-md py-3 px-4">
                  <div className="mb-4">
                    <p className="bg-[#EBF5FE] text-blue-500 cursor-default py-2 px-3 w-fit rounded-lg mb-2">
                      {dataDetail.label}
                    </p>
                    <p className="text-[24px] mb-1 font-semibold">
                      {dataDetail.name}
                    </p>
                    {dataDetail.packaging ? (
                      <p className="text-gray-500 mb-2">
                        {dataDetail.packaging}
                      </p>
                    ) : null}
                    <p>PT. Digital Pharma Andalan Indonesia</p>
                  </div>

                  <div className="mb-4">
                    <p className="text-[24px] text-red-500 font-semibold mb-2">
                      {toIDR(getPricePerPiece())}
                    </p>
                    {getAppliedTier(dataDetail.discount, orderQty) ? (
                      <div className="flex gap-2 items-center">
                        <p className="text-gray-500">
                          <s>{toIDR(dataDetail.base_price)}</s>
                        </p>
                        <p className="bg-red-100 text-red-500 p-1 rounded-sm font-semibold">
                          {
                            getAppliedTier(dataDetail.discount, orderQty)
                              .percent
                          }
                          %
                        </p>
                      </div>
                    ) : null}
                  </div>

                  <div className="w-full relative">
                    {dataDetail.discount &&
                    Array.isArray(dataDetail.discount) &&
                    dataDetail.discount.length > 2 ? (
                      <div
                        className="z-5 bg-white rounded-full flex items-center justify-center p-1 border cursor-pointer hover:bg-gray-100 w-[28px] h-[28px] absolute left-0 top-0 bottom-0 my-auto -translate-x-[12px]"
                        id="slideLeft"
                        onClick={() =>
                          (document.getElementById(
                            "containerScrollDetail",
                          ).scrollLeft -= 300)
                        }
                      >
                        <FaChevronLeft />
                      </div>
                    ) : null}
                    {dataDetail.discount &&
                    Array.isArray(dataDetail.discount) &&
                    dataDetail.discount.length > 2 ? (
                      <div
                        className="scroll-smooth z-5 bg-white rounded-full flex items-center justify-center p-1 border cursor-pointer hover:bg-gray-100 w-[28px] h-[28px] absolute right-0 top-0 bottom-0 my-auto translate-x-[14px]"
                        id="slideRight"
                        onClick={() =>
                          (document.getElementById(
                            "containerScrollDetail",
                          ).scrollLeft += 300)
                        }
                      >
                        <FaChevronRight />
                      </div>
                    ) : null}
                    <div
                      className="scroll-smooth w-full overflow-x-auto hide-scroll flex mb-4 flex gap-2 items-center p-2 z-0"
                      id="containerScrollDetail"
                    >
                      {dataDetail.discount &&
                        Array.isArray(dataDetail.discount) &&
                        dataDetail.discount.length > 0 &&
                        dataDetail.discount[0].percent > 0 &&
                        dataDetail.discount.map((item, i) => (
                          <div
                            className={`rounded-lg overflow-hidden shadow-md min-w-[200px] ${
                              getAppliedTier(dataDetail.discount, orderQty) ===
                              item
                                ? "border-red-500 border-2"
                                : ""
                            }`}
                          >
                            <div className="w-full bg-red-500 px-4 py-1 flex justify-between gap-2">
                              <p className="text-[#FDF150] font-bold text-center">
                                Diskon {formatPercent(item.percent)}
                              </p>
                            </div>
                            <div
                              className={`w-full px-4 py-1 ${
                                getAppliedTier(
                                  dataDetail.discount,
                                  orderQty,
                                ) === item
                                  ? "bg-red-100"
                                  : "bg-white"
                              }`}
                            >
                              <p className="whitespace-nowrap">
                                {item.max_qty == null
                                  ? `Min. Beli ${item.min_qty}`
                                  : `Beli ${item.min_qty} - ${item.max_qty}`}
                              </p>
                              <p className="text-[10px] p-0 m-0">
                                <s>{toIDR(dataDetail.base_price)}</s>
                              </p>
                              <p className="text-[16px] text-red-500 font-semibold">
                                {toIDR(
                                  dataDetail.base_price *
                                    ((100 - item.percent) / 100),
                                )}
                              </p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className="mb-4 flex gap-2 items-center">
                    <p>Stok:</p>
                    <p className="py-1 px-4 text-[#4FB33C] border-2 border-[#4FB33C] bg-[#F1FAF0] rounded-full">
                      Tersedia {dataDetail.stock} {dataDetail.uom}
                    </p>
                  </div>
                  {quota ? (
                    <div className="mb-4">
                      <p>{quotaDetailLabel(quota)}</p>
                      {quota.remaining === 0 ? (
                        <p className="text-red-500">Kuota bulan ini habis</p>
                      ) : null}
                    </div>
                  ) : null}

                  {/* Make into swipper class */}
                  {/* <div className="mb-4">
                  <div className={`w-[200px] rounded-lg whitespace-nowrap`}>
                    <div
                      className={`flex justify-between w-full bg-[#C62828] text-white rounded-t-lg p-2 ${
                        true
                          ? "border-t-2 border-l-2 border-r-2 border-red-500"
                          : ""
                      }`}
                    >
                      <p className="text-yellow-500 font-bold">Diskon 30%</p>
                      <p>Beli &gt; 23 pcs</p>
                    </div>
                    <div
                      className={`w-full rounded-b-lg p-2 text-right ${
                        true
                          ? "bg-[#FFE7E7] border-b-2 border-l-2 border-r-2 border-red-500"
                          : "bg-white"
                      }`}
                    >
                      <p className="text-gray-500">
                        <s>{toIDR(0)}</s>
                      </p>
                      <p className="font-bold text-red-500 text-[20px]">
                        {toIDR(0)}
                      </p>
                    </div>
                  </div>
                </div> */}

                  <div className="mb-4 flex items-center gap-4">
                    <div className=" flex items-center gap-4">
                      <div className="flex items-center gap-2 w-full rounded-md border-2 p-2 bg-white w-fit">
                        <button
                          type="button"
                          className={`text-[16px] hover:text-gray-300 text-gray-500 disabled:text-gray-300`}
                          disabled={orderQty === 0 || maxQty === 0}
                          onClick={() => setOrderQty(orderQty - 1)}
                        >
                          <FaMinus />
                        </button>
                        <input
                          className="w-full text-center focus:outline-none"
                          type="text"
                          value={orderQty}
                          disabled={maxQty === 0}
                          onChange={(e) => {
                            if (!isNaN(Number(e.target.value)))
                              setOrderQty(
                                Number(e.target.value) > maxQty
                                  ? maxQty
                                  : Number(e.target.value),
                              );
                          }}
                        />
                        <button
                          type="button"
                          className={`text-[16px] hover:text-gray-300 text-gray-500 disabled:text-gray-300`}
                          disabled={orderQty >= maxQty || maxQty === 0}
                          onClick={() => setOrderQty(orderQty + 1)}
                        >
                          <FaPlus />
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="text-white bg-[#94C679] hover:bg-gray-300 rounded-md w-full px-4 py-2 text-[14px] w-fit min-w-[200px] disabled:bg-gray-300"
                      onClick={handleAddToCart}
                      disabled={
                        addToCart.isLoading || orderQty === 0 || maxQty === 0
                      }
                    >
                      {addToCart.isLoading ? (
                        <CircularProgress size={10} />
                      ) : (
                        <>
                          {dataDetail.stock === 0 ? (
                            "Habis"
                          ) : (
                            <>
                              + Tambah ({toIDR(getPricePerPiece() * orderQty)})
                            </>
                          )}
                        </>
                      )}
                    </button>
                  </div>
                  {quota &&
                  quota.remaining > 0 &&
                  orderQty === quota.remaining ? (
                    <p className="text-gray-500 -mt-2 mb-4">
                      {quotaCapHint(quota)}
                    </p>
                  ) : null}
                </div>
              </div>

              {dataDetail.bonus &&
              dataDetail.bonus.length > 0 &&
              dataDetail.bonus[0].qty_bonus > 0 ? (
                <div className="mb-4 text-[14px] py-3 px-4 bg-white rounded-md">
                  <p className="mb-2 text-[18px] font-semibold">Bonus</p>

                  <ul className="list-disc pl-4">
                    {dataDetail.bonus.map((item, i) => (
                      <li>
                        Gratis {item.qty_bonus} produk "{item.name}" untuk min.
                        pembelian sebanyak {item.min_qty}.
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="mb-4 text-[14px] py-3 px-4 bg-white rounded-md">
                <p className="mb-2 text-[18px] font-semibold">
                  Informasi Produk
                </p>
                <p className="text-[#626F86]">Kandungan/Komposisi</p>
                <p className="mb-4">
                  {dataDetail.komposisi && dataDetail.komposisi !== ""
                    ? dataDetail.komposisi
                    : "-"}{" "}
                </p>

                <p className="text-[#626F86]">Tanggal Kadaluwarsa</p>
                <p className="mb-4">
                  {dataDetail.expired_date && dataDetail.expired_date !== ""
                    ? moment(dataDetail.expired_date).format("DD/MM/YYYY")
                    : "-"}{" "}
                </p>

                <p className="text-[#626F86]">Kemasan</p>
                <p className="mb-4">{dataDetail.uom} </p>

                {dataDetail.packaging ? (
                  <>
                    <p className="text-[#626F86]">Kemasan Lengkap</p>
                    <p className="mb-4">{dataDetail.packaging}</p>
                  </>
                ) : null}

                <p className="text-[#626F86]">Kategori Produk</p>
                <p className="mb-4">{dataDetail.kategori_produk} </p>

                <p className="text-[#626F86]">Golongan Obat</p>
                <p className="mb-4">{dataDetail.drug_class || "-"} </p>

                <p className="text-[#626F86]">Principal</p>
                <p className="mb-4">{dataDetail.principal} </p>

                <p className="text-[#626F86]">Nomor Izin Edar (NIE)</p>
                <p>
                  {dataDetail.nie && dataDetail.nie !== ""
                    ? dataDetail.nie
                    : "-"}
                </p>
              </div>

              <div className="mb-4 text-[14px] py-3 px-4 bg-white rounded-md">
                <p className="mb-2 text-[18px] font-semibold">
                  Syarat & Ketentuan Retur
                </p>
                <p>{dataDetail.tnc}</p>
              </div>
            </div>
          ) : isFetchingDetail ? (
            <CircularProgress size={20} />
          ) : (
            <p>Tidak ada data detail produk</p>
          )}
        </div>
      </div>

      <SuccessAddCartPopup
        open={openSuccess}
        setOpen={setOpenSuccess}
        closeFunc={() => {
          setOrderQty(1);
          refetch();
        }}
      />

      <ErrorPopup
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
      <PbfRefusalDialog
        error={refusal}
        title="Tidak dapat ditambahkan"
        onClose={() => setRefusal(null)}
      />
    </div>
  );
};

export default PurchaseProductDetail;
